<template>
    <div class="h-screen w-screen cursor-move overflow-hidden">

        <div
            id="scene"
            :style="`grid-template-columns: repeat(${gridColumns}, 200px)`"
            class="anarchive-grid grid grid-rows-1 auto-rows-auto"
            @pointerdown="handleMouseDown"
            @pointerup="handleMouseUp"
        >
            <div v-for="colNumber in gridColumns" class="flex flex-col gap-20 justify-center odd:pb-10 even:pt-10 anarchive-grid-columns">
                <OrganismImageHover
                    v-for="event in filteredEvents.slice(gridRows*(colNumber-1), (gridRows*(colNumber-1))+gridRows)"
                    :key="`event-${event.id}`"
                    :event="event"
                    @pointerdown.native="clickedEvent = event"
                />
            </div>
        </div>

    </div>
</template>

<script>
import panzoom from "panzoom";
import OrganismImageHover from "../components/anarchive/organisms/OrganismImageHover.vue";
import {mapGetters} from "vuex";
import LayoutAnarchive from "../layouts/LayoutAnarchive.vue";
import TransitionFade from "../transitions/TransitionFade.vue";
import _ from "lodash";

export default {
    name: "PageAnarchiveGridV2",
    components: {TransitionFade, LayoutAnarchive, OrganismImageHover},

    data() {
        return {
            infinityGrid: null,
            clickHandling: false,
            clickedEvent: null
        }
    },

    computed: {
        ...mapGetters('event', {
            events: 'getFilteredAnarchiveEvents',
        }),

        filteredEvents() {
            return _.slice(_.shuffle(this.events), 0, 400);
        },

        gridRows() {
            return Math.ceil(Math.sqrt(this.filteredEvents.length));
        },

        gridColumns() {
            return Math.ceil(this.filteredEvents.length / this.gridRows);
        },

        screenCenterPos() {
            return  {
                x: (window.innerWidth / 2),
                y: (window.innerHeight / 2)
            }
        }
    },

    methods: {
        handlePlus () {
            this.infinityGrid.pause()
            this.infinityGrid.smoothZoom(this.screenCenterPos.x, this.screenCenterPos.y,1.5)
            this.infinityGrid.resume()
        },

        handleMinus(){
            this.infinityGrid.pause()
            this.infinityGrid.smoothZoom(this.screenCenterPos.x, this.screenCenterPos.y,0.5)
            this.infinityGrid.resume()
        },

        centerFocus(){
            let element = document.querySelector('#scene')
            if (!element) return

            this.infinityGrid.pause()
            const containerRect = element.getBoundingClientRect();

            // Calculate the offset needed to center the element on the screen
            const offset = {
                x: (window.innerWidth / 2) - (containerRect.width / 2),
                y: (window.innerHeight / 2) - (containerRect.height / 2),
            };

            this.infinityGrid.moveTo(offset.x, offset.y);
            this.infinityGrid.resume()
        },

        observeSceneSize() {
            // Create a ResizeObserver to watch for changes in the scene size
            const resizeObserver = new ResizeObserver(() => {
                this.centerFocus();
            });

            // Start observing the scene element
            resizeObserver.observe(document.querySelector("#scene"));
        },

        handleMouseDown(event) {
            if (event.target.classList.contains('anarchive-grid-columns')){
                this.clickedEvent = null
            }

            if (!this.clickHandling){
                this.startX = event.clientX;
                this.startY = event.clientY;
                this.cursorMoved = false;
            }
        },

        handleMouseUp() {
            if (!this.clickHandling){
                this.clickHandling = true
                setTimeout(() => {
                    if (!this.cursorMoved && this.clickedEvent) {
                        this.$router.push({ name: 'event.view', params: { id: this.clickedEvent.id, slug: this.clickedEvent.slug } })
                    }
                    this.clickHandling = false
                }, 300);
            }
        },

        handlePointerMove(event) {
            if (Math.abs(event.clientX - this.startX) > 5 || Math.abs(event.clientY - this.startY) > 5) {
                this.cursorMoved = true;
            }
        },
    },

    mounted() {
        window.addEventListener('pointermove', this.handlePointerMove);

        let element = document.querySelector('#scene')

        this.infinityGrid = panzoom(element, {
            maxZoom: 3,
            minZoom: 1,
            initialZoom: window.innerWidth < 640 ? 1.75 : 2,
            bounds: true,
            boundsPadding: 0.6,
            beforeWheel: (e) => {
                let element = document.querySelector('.anarchive-grid')
                let matrix = new DOMMatrix(getComputedStyle(element).transform);

                if (e.shiftKey) {
                    this.infinityGrid.moveTo(matrix.e + (e.deltaY), matrix.f);
                }
                else if (!e.altKey) {
                    this.infinityGrid.moveTo(matrix.e + e.deltaX, matrix.f + -e.deltaY);
                }

                // allow wheel-zoom only if altKey is down. Otherwise - ignore
                return !e.altKey;
            }
        })

        this.observeSceneSize();
    },

    beforeDestroy() {
        window.removeEventListener('pointermove', this.handlePointerMove);
    },
}
</script>

<style scoped>
.anarchive-grid {
    width: fit-content;
    display: grid;
}
</style>
